import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import {
  KEY_ROW_INDEX,
  PARSE_EXCEL_ERROR_CODE,
  COLUMN_NO,
  SENDER_POI,
  DROP_OFF_POI,
  COLUMN_SENDER_NAME,
  COLUMN_SENDER_PHONE,
  COLUMN_SENDER_ADDRESS,
  COLUMN_SENDER_ADDRESS_DETAIL,
  COLUMN_RECEIVER_NAME,
  COLUMN_RECEIVER_PHONE,
  COLUMN_RECEIVER_ADDRESS,
  COLUMN_RECEIVER_ADDRESS_DETAIL,
  COLUMN_ITEM_WEIGHT,
  COLUMN_PARCEL,
  COLUMN_COD,
  COLUMN_DELIVERY_INSTRUCTION,
  COLUMN_REFERENCE_CODE,
  COLUMN_SERVICE_TYPE,
  COLUMN_VEHICLE_TYPE,
  COLUMN_MULTI_STOP_BATCH,
  COLUMN_MULTI_STOP_SEQUENCE_NUMBER,
  DEFAULT_XLSX_WORK_SHEET_SUFFIX,
  COLUMN_SENDER_ADMIN_AREA,
  COLUMN_RECEIVER_ADMIN_AREA,
  COLUMN_PARCEL_SIZE,
} from 'constants/new-bulk-upload-const';

import { isPhoneNumberFormat } from 'helper/check-helper';
import {
  codAdditionalServiceID,
  deliveryIDPrefixes,
  genDeliveryID,
} from 'constants/anytime-booking-constants';

const codAS = {
  id: codAdditionalServiceID,
  data: {
    enabled: true,
  },
};

const validateAddress = data => {
  if (!data) {
    return data;
  }
  if (typeof data === 'number') {
    return data.toString();
  }
  return data.trim();
};

const validateAndFormatPhoneNumber = data => {
  if (!isPhoneNumberFormat(data)) {
    return data;
  }
  return String(data).replace(/\D/g, '');
};

const throwInvalidError = (fieldName, rowIndex) => {
  throw {
    fieldName,
    rowIndex,
    errorCode: PARSE_EXCEL_ERROR_CODE.INVALID_DATA_ERROR,
  };
};

// Split it here so it doesn't occupied memory for each row
const validateItemWeight = (data, rowIndex, metaData) => {
  if (data < 0 || data > _get(metaData, 'maxWeight', 0)) {
    throwInvalidError(COLUMN_ITEM_WEIGHT, rowIndex);
  }
  const num = Number(data);
  if (Number.isNaN(num)) {
    return data;
  }
  return Math.ceil(num);
};

const validateParcelSize = (data, rowIndex, metaData) => {
  if (!data) {
    return undefined;
  }
  return data;
};

const validateServiceType = (data, rowIndex, metaData) => {
  if (!data) {
    return undefined;
  }

  for (const [code, name] of Object.entries(
    _get(metaData, 'serviceTypeNames', {}),
  )) {
    if (data === name) {
      return code;
    }
  }

  throwInvalidError(COLUMN_SERVICE_TYPE, rowIndex);
};

const validateAdminArea = (data, rowIndex, metaData) => {
  if (!data) {
    return undefined;
  }
  const adminAreas = _get(metaData, 'adminAreas');
  if (_isEmpty(adminAreas)) {
    return undefined;
  }
  return adminAreas.find(({ name }) => name === data) || undefined;
};

const validateVehicleType = (data, rowIndex, metaData) => {
  if (!data) {
    return undefined;
  }
  for (const [code, name] of Object.entries(
    _get(metaData, 'vehicleTypeNames', {}),
  )) {
    if (data === name) {
      return code;
    }
  }

  throwInvalidError(COLUMN_VEHICLE_TYPE, rowIndex);
};

const validateIsNullabelInteger = (data, rowIndex, fieldName) => {
  if (!data) {
    return undefined;
  }

  const num = Number(data);
  if (Number.isNaN(num) || !Number.isInteger(num)) {
    throwInvalidError(fieldName, rowIndex);
  }
  return num;
};

const validateName = data => String(data).trim();

const validateAndFormatAddressDetail = data => String(data || '').trim();

const validateCOD = (data, _, metaData) => {
  if (
    data === null ||
    data === undefined ||
    !_get(metaData, ['cod', 'currency', 'code'], null)
  ) {
    return undefined;
  }
  const num = Number(data);
  if (Number.isNaN(num)) {
    return data;
  }
  return num;
};

const validateMSD = (data, rowIndex, metaData, fieldName) => {
  if (!_get(metaData, 'supportMSD', false)) {
    return undefined;
  }
  return validateIsNullabelInteger(data, rowIndex, fieldName);
};

export const getKeyColumnFormat = featureFlags => {
  const baseColumns = {
    [COLUMN_NO]: { index: -1, required: true, name: 'no' },
    [COLUMN_SENDER_NAME]: {
      index: -1,
      required: true,
      name: 'senderName',
      validator: data => validateName(data),
    },
    [COLUMN_SENDER_PHONE]: {
      index: -1,
      required: true,
      name: 'senderPhone',
      validator: data => validateAndFormatPhoneNumber(data),
    },
    [COLUMN_SENDER_ADDRESS]: {
      index: -1,
      required: true,
      name: 'senderAddress',
      validator: validateAddress,
    },
    [COLUMN_SENDER_ADMIN_AREA]: {
      index: -1,
      required: false,
      name: 'senderAdminArea',
      validator: validateAdminArea,
    },
    [COLUMN_SENDER_ADDRESS_DETAIL]: {
      index: -1,
      required: false,
      name: 'senderAddressDetail',
      validator: data => validateAndFormatAddressDetail(data),
    },
    [COLUMN_RECEIVER_NAME]: {
      index: -1,
      required: true,
      name: 'receiverName',
      validator: data => validateName(data),
    },
    [COLUMN_RECEIVER_PHONE]: {
      index: -1,
      required: true,
      name: 'receiverPhone',
      validator: data => validateAndFormatPhoneNumber(data),
    },
    [COLUMN_RECEIVER_ADDRESS]: {
      index: -1,
      required: true,
      name: 'receiverAddress',
      validator: validateAddress,
    },
    [COLUMN_RECEIVER_ADMIN_AREA]: {
      index: -1,
      required: false,
      name: 'receiverAdminArea',
      validator: validateAdminArea,
    },
    [COLUMN_RECEIVER_ADDRESS_DETAIL]: {
      index: -1,
      required: false,
      name: 'receiverAddressDetail',
      validator: data => validateAndFormatAddressDetail(data),
    },
    [COLUMN_PARCEL_SIZE]: {
      index: -1,
      // override enableBigParcel flag as true DELVOD-25786
      required: true,
      name: 'parcelTotalSize',
      validator: validateParcelSize,
    },
    [COLUMN_ITEM_WEIGHT]: {
      index: -1,
      required: true,
      name: 'itemWeight',
      validator: validateItemWeight,
    },
    [COLUMN_PARCEL]: { index: -1, required: true, name: 'parcel' },
    [COLUMN_COD]: {
      index: -1,
      required: false,
      name: 'cod',
      validator: validateCOD,
    },
    [COLUMN_DELIVERY_INSTRUCTION]: {
      index: -1,
      required: false,
      name: 'deliveryInstruction',
    },
    [COLUMN_REFERENCE_CODE]: {
      index: -1,
      required: false,
      name: 'referenceCode',
    },
    [COLUMN_SERVICE_TYPE]: {
      index: -1,
      required: false,
      name: 'serviceType',
      validator: validateServiceType,
    },
    [COLUMN_VEHICLE_TYPE]: {
      index: -1,
      required: false,
      name: 'vehicleType',
      validator: validateVehicleType,
    },
  };

  // Only include MSD columns when enableMSD is true
  if (_get(featureFlags, 'enableMSD', false)) {
    baseColumns[COLUMN_MULTI_STOP_BATCH] = {
      index: -1,
      required: false,
      name: 'batch',
      validator: (data, rowIndex, metaData) =>
        validateMSD(data, rowIndex, metaData, COLUMN_MULTI_STOP_BATCH),
    };
    baseColumns[COLUMN_MULTI_STOP_SEQUENCE_NUMBER] = {
      index: -1,
      required: false,
      name: 'sequenceNumber',
      validator: (data, rowIndex, metaData) =>
        validateMSD(
          data,
          rowIndex,
          metaData,
          COLUMN_MULTI_STOP_SEQUENCE_NUMBER,
        ),
    };
  }

  return baseColumns;
};

export const getExcelReduxName = (col, keyColumnFormat, featureFlags) => {
  if (!keyColumnFormat) {
    keyColumnFormat = getKeyColumnFormat(featureFlags);
  }
  const data = _get(keyColumnFormat, [col]);
  if (data) {
    return data.name;
  }
  return '';
};

export const parseColumnIndexes = (workSheet, featureFlags) => {
  const keyColumnFormat = getKeyColumnFormat(featureFlags);
  const row = workSheet.getRow(KEY_ROW_INDEX);
  row.eachCell((cell, colNumber) => {
    if (_get(keyColumnFormat, cell.value)) {
      keyColumnFormat[cell.value].index = colNumber;
    }
  });

  return keyColumnFormat;
};

export const getUploadWorksheetFromWorkbook = workbook => {
  if (_isEmpty(workbook.worksheets)) {
    throw {
      errorCode: PARSE_EXCEL_ERROR_CODE.TEMPLATE_ERROR,
    };
  }

  let parsedWorkSheet;
  workbook.eachSheet((ws, _) => {
    if (
      !parsedWorkSheet &&
      _get(ws, 'name', '').endsWith(DEFAULT_XLSX_WORK_SHEET_SUFFIX)
    ) {
      parsedWorkSheet = ws;
    }
  });
  if (!parsedWorkSheet) {
    throw {
      errorCode: PARSE_EXCEL_ERROR_CODE.NOT_FOUND_WORK_SHEET,
    };
  }
  return parsedWorkSheet;
};

export const rowToDeliveryData = (
  row,
  categoryReverseMap,
  keyColumnFormat,
  featureFlags,
) => {
  const pickUp = {
    poi: _get(row, SENDER_POI),
    name: _get(
      row,
      getExcelReduxName(COLUMN_SENDER_NAME, keyColumnFormat, featureFlags),
    ),
    phone: _get(
      row,
      getExcelReduxName(COLUMN_SENDER_PHONE, keyColumnFormat, featureFlags),
    ),
    locationNote: _get(
      row,
      getExcelReduxName(
        COLUMN_SENDER_ADDRESS_DETAIL,
        keyColumnFormat,
        featureFlags,
      ),
    ),
    senderAdminArea: _get(
      row,
      getExcelReduxName(
        COLUMN_SENDER_ADMIN_AREA,
        keyColumnFormat,
        featureFlags,
      ),
    ),
  };

  const itemTypeName = _get(
    row,
    getExcelReduxName(COLUMN_PARCEL, keyColumnFormat, featureFlags),
  );
  const itemTypeID = _get(categoryReverseMap, itemTypeName);
  const itemType =
    itemTypeID && itemTypeName ? `${itemTypeID}-${itemTypeName}` : undefined;

  const referenceCode = _get(
    row,
    getExcelReduxName(COLUMN_REFERENCE_CODE, keyColumnFormat, featureFlags),
  );
  const codInput = _get(
    row,
    getExcelReduxName(COLUMN_COD, keyColumnFormat, featureFlags),
  );
  const codAmount = isNaN(codInput) ? 0 : +codInput;
  const dropOffs = [
    {
      poi: _get(row, DROP_OFF_POI),
      name: _get(
        row,
        getExcelReduxName(COLUMN_RECEIVER_NAME, keyColumnFormat, featureFlags),
      ),
      phone: _get(
        row,
        getExcelReduxName(COLUMN_RECEIVER_PHONE, keyColumnFormat, featureFlags),
      ),
      locationNote: _get(
        row,
        getExcelReduxName(
          COLUMN_RECEIVER_ADDRESS_DETAIL,
          keyColumnFormat,
          featureFlags,
        ),
      ),
      deliveryInstruction: _get(
        row,
        getExcelReduxName(
          COLUMN_DELIVERY_INSTRUCTION,
          keyColumnFormat,
          featureFlags,
        ),
      ),
      cod: codAmount,
      itemType,
      itemWeight: _get(
        row,
        getExcelReduxName(COLUMN_ITEM_WEIGHT, keyColumnFormat, featureFlags),
        0,
      ),
      rowIndex: _get(
        row,
        getExcelReduxName(COLUMN_NO, keyColumnFormat, featureFlags),
        0,
      ),
      receiverAdminArea: _get(
        row,
        getExcelReduxName(
          COLUMN_RECEIVER_ADMIN_AREA,
          keyColumnFormat,
          featureFlags,
        ),
      ),
    },
  ];

  const additionalServices = [];

  if (codAmount && codAmount > 0) {
    additionalServices.push(codAS);
  }

  return {
    deliveryID: genDeliveryID(deliveryIDPrefixes.BulkUpload),
    pickUp,
    dropOffs,
    serviceType: _get(
      row,
      getExcelReduxName(COLUMN_SERVICE_TYPE, keyColumnFormat, featureFlags),
    ),
    vehicleType: _get(
      row,
      getExcelReduxName(COLUMN_VEHICLE_TYPE, keyColumnFormat, featureFlags),
    ),
    additionalServices,
    referenceCode,
    parcelTotalSize: _get(
      row,
      getExcelReduxName(COLUMN_PARCEL_SIZE, keyColumnFormat, featureFlags),
    ),
  };
};

export const addMoreStepToDeliveryData = (
  row,
  deliveryData,
  categoryReverseMap,
  keyColumnFormat,
  featureFlags,
) => {
  const itemTypeName = _get(
    row,
    getExcelReduxName(COLUMN_PARCEL, keyColumnFormat, featureFlags),
  );
  const itemTypeID = _get(categoryReverseMap, itemTypeName);
  const itemType =
    itemTypeID && itemTypeName ? `${itemTypeID}-${itemTypeName}` : undefined;

  const newDropOff = {
    poi: _get(row, DROP_OFF_POI),
    name: _get(
      row,
      getExcelReduxName(COLUMN_RECEIVER_NAME, keyColumnFormat, featureFlags),
    ),
    phone: _get(
      row,
      getExcelReduxName(COLUMN_RECEIVER_PHONE, keyColumnFormat, featureFlags),
    ),
    locationNote: _get(
      row,
      getExcelReduxName(
        COLUMN_RECEIVER_ADDRESS_DETAIL,
        keyColumnFormat,
        featureFlags,
      ),
    ),
    deliveryInstruction: _get(
      row,
      getExcelReduxName(
        COLUMN_DELIVERY_INSTRUCTION,
        keyColumnFormat,
        featureFlags,
      ),
    ),
    cod: _get(
      row,
      getExcelReduxName(COLUMN_COD, keyColumnFormat, featureFlags),
    ),
    itemType,
    itemWeight: _get(
      row,
      getExcelReduxName(COLUMN_ITEM_WEIGHT, keyColumnFormat, featureFlags),
      0,
    ),
    rowIndex: _get(
      row,
      getExcelReduxName(COLUMN_NO, keyColumnFormat, featureFlags),
      0,
    ),
    receiverAdminArea: _get(
      row,
      getExcelReduxName(
        COLUMN_RECEIVER_ADMIN_AREA,
        keyColumnFormat,
        featureFlags,
      ),
    ),
  };
  deliveryData.dropOffs.push(newDropOff);
  if (
    _get(row, getExcelReduxName(COLUMN_COD, keyColumnFormat, featureFlags)) &&
    !deliveryData.additionalServices.find(as => as.id === codAS.id)
  ) {
    deliveryData.additionalServices.push(codAS);
  }
  deliveryData.isNotPresetSequence = _get(row, 'isNotPresetSequence', false);
};

export const deliveriesDataToRows = (deliveries, featureFlags) => {
  const rows = [];

  deliveries.forEach(delivery => {
    const {
      detail: { pickUp, dropOffs, configuration: { serviceType, vehicleType } },
    } = delivery;

    const row = {};

    row[COLUMN_SENDER_NAME] = _get(pickUp, ['name']);
    row[COLUMN_SENDER_PHONE] = _get(pickUp, ['phone']);
    row[COLUMN_SENDER_ADDRESS] = _get(pickUp, [
      'poi',
      'address',
      'combined_address',
    ]);
    row[COLUMN_SENDER_ADDRESS_DETAIL] = _get(pickUp, ['locationNote']);
    row[COLUMN_SENDER_ADMIN_AREA] = _get(pickUp, ['senderAdminArea', 'name']);

    row[COLUMN_SERVICE_TYPE] = serviceType;
    row[COLUMN_VEHICLE_TYPE] = vehicleType;

    const firstDropOff = dropOffs[0];

    row[COLUMN_NO] = _get(firstDropOff, ['rowIndex']);
    row[COLUMN_RECEIVER_NAME] = _get(firstDropOff, ['name']);
    row[COLUMN_RECEIVER_PHONE] = _get(firstDropOff, ['phone']);
    row[COLUMN_RECEIVER_ADDRESS] = _get(firstDropOff, [
      'poi',
      'address',
      'combined_address',
    ]);
    row[COLUMN_RECEIVER_ADDRESS_DETAIL] = _get(firstDropOff, ['locationNote']);
    row[COLUMN_DELIVERY_INSTRUCTION] = _get(firstDropOff, [
      'deliveryInstruction',
    ]);
    row[COLUMN_COD] = _get(firstDropOff, ['cod']);
    const [, catName] = _get(firstDropOff, ['itemType'], '').split('-');
    row[COLUMN_PARCEL] = catName;
    row[COLUMN_ITEM_WEIGHT] = _get(firstDropOff, ['itemWeight']);
    row[COLUMN_RECEIVER_ADMIN_AREA] = _get(firstDropOff, [
      'receiverAdminArea',
      'name',
    ]);

    // If MSD is disabled or there's only one drop-off, add single row
    if (!_get(featureFlags, 'enableMSD', false) || dropOffs.length < 2) {
      rows.push(row);
      return;
    }

    // Only add MSD columns when enableMSD is true and there are multiple drop-offs
    row[COLUMN_MULTI_STOP_BATCH] = row[COLUMN_NO];

    dropOffs.forEach((dropOff, index) => {
      const msdRow = _cloneDeep(row);

      msdRow[COLUMN_RECEIVER_NAME] = _get(dropOff, ['name']);
      msdRow[COLUMN_RECEIVER_PHONE] = _get(dropOff, ['phone']);
      msdRow[COLUMN_RECEIVER_ADDRESS] = _get(dropOff, [
        'poi',
        'address',
        'combined_address',
      ]);
      msdRow[COLUMN_RECEIVER_ADDRESS_DETAIL] = _get(dropOff, ['locationNote']);
      msdRow[COLUMN_DELIVERY_INSTRUCTION] = _get(dropOff, [
        'deliveryInstruction',
      ]);
      msdRow[COLUMN_COD] = _get(dropOff, ['cod']);
      const [, catName] = _get(dropOff, ['itemType'], '').split('-');
      msdRow[COLUMN_PARCEL] = catName;
      msdRow[COLUMN_ITEM_WEIGHT] = _get(dropOff, ['itemWeight']);

      msdRow[COLUMN_NO] = _get(dropOff, ['rowIndex']);
      msdRow[COLUMN_MULTI_STOP_SEQUENCE_NUMBER] = index + 1;

      rows.push(msdRow);
    });
  });
  return rows;
};

export default {};
